import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { categoryList } from './utils'

const DISPLAY_NOTE_CATEGORIES = {
  夏休み留学: '7/20(日)〜8/24(日)の夏休み期間にいける留学先です。7月留学限定で、新幹線の予約代行サービスを実施中です。',
  ふるさと納税: 'ふるさと納税を利用し留学費用の一部を支払うことができる留学先です',
  赤ちゃんといっしょ: '寝室の布団対応が可能かつバスルームにバスタブがある宿をもつ留学先です',
  留学レンタカー: '地域の方の協力のもと無料・格安でレンタカーをご利用いただける留学先です',
  園から写真共有: '園からアプリなどを通してお子さまの活動写真を見ることができる留学先です',
}

const EXTERNAL_LINKS = {
  夏休み留学: 'https://hoikuen-ryugaku.com/magazine/shinkansenreservationservice',
  ふるさと納税: 'https://hoikuen-ryugaku.com/ryugakusakinouzei',
  留学レンタカー: 'https://hoikuen-ryugaku.com/magazine/11',
}

type SelectedCategory = {
  key?: string
  value?: string
}

const DEFAULT_SELECTED_CATEGORY: SelectedCategory = {
  key: '',
  value: '',
}

export const CategoryFilter = () => {
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>(DEFAULT_SELECTED_CATEGORY)
  const [nearestActiveItem, setNearestActiveItem] = useState<number>(null)
  const nearestItemRef = useRef(null)
  const { t } = useTranslation()
  const categories = useMemo(() => categoryList(t), [])

  const handleFindTheNearestActiveItem = (selectedCategory: SelectedCategory) => {
    const activeCategory = categories.find((item) => item.value === selectedCategory.value)
    return activeCategory?.id
  }

  useEffect(() => {
    const pathname = window.location.pathname
    const queryString = window.location.search
    const queryParams = new URLSearchParams(queryString)
    let updateSelectedCategory = { ...selectedCategory }

    queryParams.forEach((value, key) => {
      if (key === 'feature' || key === 'room_environment' || key === 'service_type') {
        updateSelectedCategory = { key, value }
      }
      if (key === 'no_car' && document.documentElement.lang === 'en' && !updateSelectedCategory.key) {
        updateSelectedCategory = { key, value }
      }
    })
    if (pathname === '/all') {
      updateSelectedCategory = { key: 'all', value: t('top.category_list.all') }
    }

    const nearestItem = handleFindTheNearestActiveItem(updateSelectedCategory)

    setNearestActiveItem(nearestItem)
    setSelectedCategory(updateSelectedCategory)
  }, [])

  useEffect(() => {
    if (nearestItemRef.current) {
      nearestItemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
    }
  }, [nearestItemRef, nearestActiveItem])

  const handleGenerateFilterQueryURL = (updateSelectedCategory: SelectedCategory) => {
    const queryString = window.location.search
    const currentQueryParams = new URLSearchParams(queryString)

    const listQueryCategory = ['feature', 'room_environment', 'no_car', 'service_type']

    if (updateSelectedCategory.key === 'clear') {
      listQueryCategory.forEach((category) => currentQueryParams.delete(category))
    } else {
      listQueryCategory.forEach((category) => {
        if (category !== updateSelectedCategory.key) {
          currentQueryParams.delete(category)
        } else {
          currentQueryParams.set(category, updateSelectedCategory.value)
        }
      })
    }
    if (currentQueryParams.get('page')) {
      currentQueryParams.set('page', '1')
    }

    return currentQueryParams.toString()
  }

  const handleClearAllCategory = () => {
    const queryURL = handleGenerateFilterQueryURL({ key: 'clear', value: '' })
    setSelectedCategory(DEFAULT_SELECTED_CATEGORY)
    const basePath = window.location.pathname === '/all' ? '/' : window.location.pathname
    window.location.href = queryURL ? `${basePath}?${queryURL}` : basePath
  }

  const handleOnClickCategory = (categoryValue: string, key: string) => {
    if (key === 'clear') {
      handleClearAllCategory()
      return
    }

    if (window.location.pathname === '/all') {
      const currentParams = new URLSearchParams(window.location.search)
      const resetKeys = ['feature', 'room_environment', 'service_type']
      resetKeys.forEach((k) => currentParams.delete(k))

      setSelectedCategory({ key, value: categoryValue })
      const query = currentParams.toString()
      window.location.href = query ? `/all?${query}` : '/all'
      return
    }

    const updateSelectedCategory = { key, value: categoryValue }
    setSelectedCategory(updateSelectedCategory)
    const queryURL = handleGenerateFilterQueryURL(updateSelectedCategory)
    const basePath = window.location.pathname === '/all' ? '/' : window.location.pathname
    window.location.href = queryURL ? `${basePath}?${queryURL}` : basePath
  }

  const getNoteText = () => {
    return DISPLAY_NOTE_CATEGORIES[selectedCategory.value] || ''
  }

  const getExternalLink = () => {
    return EXTERNAL_LINKS[selectedCategory.value] || ''
  }

  return (
    <>
      <div className={`category-wrapper category-${document.documentElement.lang}`}>
        {categories.map((item) => {
          const isActive = selectedCategory.value === item.value || (item.type === 'clear' && !selectedCategory.value)

          return (
            <button
              ref={item.id === nearestActiveItem ? nearestItemRef : null}
              onClick={() => handleOnClickCategory(item.value, item.type)}
              className={`item-category ${isActive ? 'item-category-active' : ''}`}
              key={item.value}
            >
              {isActive ? <item.icon active={true} className={`icon-category`} /> : <item.icon className={`icon-category`} />}
              <p>{item.value}</p>
            </button>
          )
        })}
      </div>
      {getNoteText() && (
        <div className="category-note">
          <div>
            <p>{getNoteText()}</p>
            {EXTERNAL_LINKS[selectedCategory.value] && (
              <a
                href={getExternalLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center mt-1 self-end category-note-link"
              >
                詳しくはこちら
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M11.0001 3C10.7349 3 10.4805 3.10536 10.293 3.29289C10.1054 3.48043 10.0001 3.73478 10.0001 4C10.0001 4.26522 10.1054 4.51957 10.293 4.70711C10.4805 4.89464 10.7349 5 11.0001 5H13.5861L7.29308 11.293C7.19757 11.3852 7.12139 11.4956 7.06898 11.6176C7.01657 11.7396 6.98898 11.8708 6.98783 12.0036C6.98668 12.1364 7.01198 12.2681 7.06226 12.391C7.11254 12.5139 7.18679 12.6255 7.28069 12.7194C7.37458 12.8133 7.48623 12.8875 7.60913 12.9378C7.73202 12.9881 7.8637 13.0134 7.99648 13.0123C8.12926 13.0111 8.26048 12.9835 8.38249 12.9311C8.50449 12.8787 8.61483 12.8025 8.70708 12.707L15.0001 6.414V9C15.0001 9.26522 15.1054 9.51957 15.293 9.70711C15.4805 9.89464 15.7349 10 16.0001 10C16.2653 10 16.5197 9.89464 16.7072 9.70711C16.8947 9.51957 17.0001 9.26522 17.0001 9V4C17.0001 3.73478 16.8947 3.48043 16.7072 3.29289C16.5197 3.10536 16.2653 3 16.0001 3H11.0001Z"
                    fill="#007CFF"
                  />
                  <path
                    d="M5 5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V15C3 15.5304 3.21071 16.0391 3.58579 16.4142C3.96086 16.7893 4.46957 17 5 17H13C13.5304 17 14.0391 16.7893 14.4142 16.4142C14.7893 16.0391 15 15.5304 15 15V12C15 11.7348 14.8946 11.4804 14.7071 11.2929C14.5196 11.1054 14.2652 11 14 11C13.7348 11 13.4804 11.1054 13.2929 11.2929C13.1054 11.4804 13 11.7348 13 12V15H5V7H8C8.26522 7 8.51957 6.89464 8.70711 6.70711C8.89464 6.51957 9 6.26522 9 6C9 5.73478 8.89464 5.48043 8.70711 5.29289C8.51957 5.10536 8.26522 5 8 5H5Z"
                    fill="#007CFF"
                  />
                </svg>
              </a>
            )}
          </div>
        </div>
      )}
    </>
  )
}
